import { Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChipInput } from "@/components/app/ChipInput";
import { ListEditor } from "@/components/intake/ListEditor";
import { cn } from "@/lib/utils";

const LOW_TIP = "We weren't sure about this one when reading the resume. Please check it.";

function Field({ id, label, low, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)} data-testid={`review-field-${id}`} data-low={low ? "true" : "false"}>
      <label htmlFor={`review-${id}`} className="flex items-center gap-1.5 text-xs font-medium text-ink-secondary">
        {label}
        {low && (
          <Tooltip>
            <TooltipTrigger asChild><button type="button" aria-label={`Low confidence: ${label}`} className="text-caution" data-testid={`review-low-${id}`}><Info size={13} aria-hidden="true" /></button></TooltipTrigger>
            <TooltipContent className="max-w-xs bg-ink text-white">{LOW_TIP}</TooltipContent>
          </Tooltip>
        )}
      </label>
      {children}
    </div>
  );
}

const lowCls = (low) => (low ? "border-[#D9BE85] bg-caution-tint/50 focus-visible:ring-caution/30" : "bg-white");

export function ReviewForm({ value, onChange, low = [] }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const text = (id, label, extra = {}) => (
    <Field id={id} label={label} low={low.includes(id)}>
      <Input id={`review-${id}`} value={value[id] ?? ""} onChange={(e) => set(id)(e.target.value)} className={cn("h-9", lowCls(low.includes(id)))} data-testid={`review-${id}`} {...extra} />
    </Field>
  );
  return (
    <div className="space-y-5" data-testid="review-form">
      <div className="grid gap-4 sm:grid-cols-2">
        {text("name", "Full name *")}
        {text("email", "Email", { type: "email" })}
        {text("phone", "Phone")}
        {text("location", "Location")}
        {text("current_title", "Current title")}
        {text("current_company", "Current company")}
        <Field id="years_experience" label="Years of experience" low={low.includes("years_experience")}>
          <Input id="review-years_experience" type="number" min="0" step="0.5" value={value.years_experience ?? ""} data-testid="review-years_experience"
            onChange={(e) => set("years_experience")(e.target.value === "" ? null : Number(e.target.value))} className={cn("h-9", lowCls(low.includes("years_experience")))} />
        </Field>
      </div>
      <Field id="skills" label="Skills" low={low.includes("skills")}>
        <div className={cn("rounded-lg", low.includes("skills") && "ring-2 ring-[#D9BE85]")}><ChipInput values={value.skills} onChange={set("skills")} testId="review-skills" /></div>
      </Field>
      <Field id="experience" label="Work history" low={low.includes("experience")}>
        <ListEditor items={value.experience} onChange={set("experience")} low={low.includes("experience")} addLabel="Add role" testId="review-experience"
          fields={[["title", "Title"], ["company", "Company"], ["start_date", "Start (YYYY-MM)"], ["end_date", "End (YYYY-MM or blank)"]]} />
      </Field>
      <Field id="education" label="Education" low={low.includes("education")}>
        <ListEditor items={value.education} onChange={set("education")} low={low.includes("education")} addLabel="Add education" testId="review-education"
          fields={[["institution", "Institution"], ["degree", "Degree"], ["field", "Field of study"], ["end_date", "Finished (YYYY-MM)"]]} />
      </Field>
      <Field id="links" label="Links">
        <ChipInput values={value.links} onChange={set("links")} placeholder="Paste a LinkedIn, GitHub or portfolio link and press Enter" testId="review-links" />
      </Field>
    </div>
  );
}
